import { useState, useRef } from "react";
import { Agent } from "../../types/agentTypes";
import { PLAN_META, PlanKey } from "../../constants/plans";
import { Phone, Trash2, Pencil } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { agentsApi } from '../../services/agentsApi';
import { toast } from '@/hooks/use-toast';

type AvatarColor = { from: string; to: string };

type Props = {
    agent: Agent;
    colorIndex?: number;
    avatarColors?: AvatarColor[];
    onShowDetail?: (agent: Agent) => void;
    onDelete?: () => void;
    onNameUpdated?: (id: string, newName: string) => void;
};

const AgentItem: React.FC<Props> = ({ agent, colorIndex = 0, avatarColors, onDelete, onNameUpdated }) => {
    const navigate = useNavigate();
    const [isEditing, setIsEditing] = useState(false);
    const [editName, setEditName] = useState(agent.name);
    const [saving, setSaving] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const handleOpen = () => {
        if (isEditing) return;
        navigate(`/agents/${agent.id}`);
    };

    const handleEditClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setEditName(agent.name);
        setIsEditing(true);
        setTimeout(() => inputRef.current?.focus(), 50);
    };

    const handleSaveName = async (e?: React.FormEvent) => {
        e?.preventDefault();
        e?.stopPropagation();
        const trimmed = editName.trim();
        if (!trimmed || trimmed === agent.name) {
            setIsEditing(false);
            return;
        }
        setSaving(true);
        try {
            await agentsApi.updateAgent(agent.id, { nombre: trimmed });
            onNameUpdated?.(agent.id, trimmed);
            toast.success('Nombre actualizado');
        } catch {
            toast.error('Error al actualizar el nombre');
        } finally {
            setSaving(false);
            setIsEditing(false);
        }
    };
    
    const handleDeleteClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onDelete?.();
    };
    
    return (
        <div
            role="button"
            tabIndex={0}
            onClick={handleOpen}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleOpen(); }}
            className="w-full h-[240px] bg-white rounded-[10px] p-5 flex flex-col relative mx-auto cursor-pointer group"
        >
            {/* Delete Button */}
            {onDelete && (
                <button
                    onClick={handleDeleteClick}
                    className="absolute top-4 right-4 p-2 rounded-lg bg-red-50 text-red-600 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-100 z-10"
                    title="Eliminar agente"
                >
                    <Trash2 size={16} />
                </button>
            )}
            {/* Profile Section */}
            <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-5">
                    {/* Profile Image / Avatar */}
                    <div className="w-[60px] h-[60px] rounded-full flex items-center justify-center overflow-hidden flex-shrink-0">
                        {agent.profileImage ? (
                            <img src={agent.profileImage} alt={agent.name} className="w-full h-full object-cover" />
                        ) : (
                            <div
                                className="w-full h-full flex items-center justify-center"
                                style={{
                                    background: avatarColors
                                        ? `linear-gradient(135deg, ${avatarColors[colorIndex]?.from || '#6366F1'}, ${avatarColors[colorIndex]?.to || '#8B5CF6'})`
                                        : 'linear-gradient(135deg, #6366F1, #8B5CF6)',
                                }}
                            >
                                <span className="text-white font-bold text-lg font-montserrat select-none">
                                    {agent.name.charAt(0).toUpperCase()}
                                </span>
                            </div>
                        )}
                    </div>
                    {/* Name and Tag */}
                    <div className="flex flex-col gap-2">
                        {isEditing ? (
                            <form onSubmit={handleSaveName} onClick={e => e.stopPropagation()} className="flex items-center gap-1">
                                <input
                                    ref={inputRef}
                                    value={editName}
                                    onChange={e => setEditName(e.target.value)}
                                    onBlur={() => handleSaveName()}
                                    onKeyDown={e => { if (e.key === 'Escape') setIsEditing(false); }}
                                    disabled={saving}
                                    className="text-foreground font-montserrat text-[15px] font-bold border-b border-primary bg-transparent outline-none w-full"
                                />
                            </form>
                        ) : (
                            <div className="flex items-center gap-1 group/name">
                                <h3 className="text-foreground font-montserrat text-[15px] font-bold">
                                    {agent.name}
                                </h3>
                                <button
                                    onClick={handleEditClick}
                                    className="opacity-0 group-hover/name:opacity-100 transition-opacity p-0.5 hover:text-primary"
                                    title="Editar nombre"
                                >
                                    <Pencil size={12} />
                                </button>
                            </div>
                        )}
                        {agent.plan && PLAN_META[agent.plan as PlanKey] ? (
                            <div className={`inline-flex items-center px-3 py-2 rounded-[20px] ${PLAN_META[agent.plan as PlanKey].badgeClass}`}>
                                <span className="font-roboto text-[10px] leading-[14px] font-medium">
                                    Plan {PLAN_META[agent.plan as PlanKey].label}
                                </span>
                            </div>
                        ) : (
                            <div className="inline-flex items-center px-3 py-2 rounded-[20px] border border-primary bg-white">
                                <span className="text-foreground font-roboto text-[10px] leading-[14px]">
                                    {agent.funnel || "Etiqueta"}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {/* Description */}
            <p className="text-foreground font-montserrat text-sm font-medium leading-normal mb-6 max-w-[255px]">
                {agent.description}
            </p>
            {/* Action area: left=phone, right=status tag */}
            <div className="mt-auto flex items-center justify-between w-full gap-4">
                {/* Left: phone icon + number */}
                <div className="flex items-center gap-2 text-sm text-maxi-text-dark">
                    <Phone className="w-4 h-4 text-maxi-text-dark" />
                    <span className="font-montserrat text-[13px] font-medium font-mono">
                        {agent.phone ? `+${agent.phone.replace(/\D/g, '')}` : '—'}
                    </span>
                </div>

                {/* Right: status tag */}
                {(() => {
                    const isActive = agent.status === 'active';
                    const bgClass = isActive ? 'bg-green-100' : 'bg-red-100';
                    const dotClass = isActive ? 'bg-green-600' : 'bg-red-600';
                    const text = isActive ? 'Conectado' : 'No Conectado';
                    return (
                        <div className={`${bgClass} inline-flex items-center px-3 py-2 rounded-[20px]`}>
                            <span className={`${dotClass} inline-block w-2 h-2 rounded-full mr-2`} />
                            <span className="text-maxi-text-dark font-roboto text-[12px] leading-[14px]">{text}</span>
                        </div>
                    );
                })()}
            </div>
        </div>
    );
};

export default AgentItem;
